import { ChevronLeftIcon } from 'lucide-react';
import { type FC, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { SidePanelProps } from '@/types';

import SidePanelInitialContent from '@/components/shared/side-panel/side-panel-initial-content';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetFooter, SheetHeader } from '@/components/ui/sheet';
import useBreakpoint from '@/hooks/use-break-point';
import useScrollRestoration from '@/hooks/use-scroll-restoration';
import { cn } from '@/lib/utils';
import useChainStore from '@/store/useChainStore';
import {
  SEARCH_PARAM_DRAWER,
  SEARCH_PARAM_INCIDENT_ID,
  SEARCH_PARAM_INCIDENT_SEARCH,
  SEARCH_PARAM_INCIDENTS,
  SEARCH_PARAM_JOURNEY_ID,
  SEARCH_PARAM_LOCK_PIN,
  SEARCH_PARAM_MONITOR,
  SEARCH_PARAM_ROUTE_LINE_NUMBER,
  SEARCH_PARAM_ROUTE_SEARCH,
  SEARCH_PARAM_ROUTE_TYPE,
  SEARCH_PARAM_SELECTED_DATE,
  SEARCH_PARAM_SHOW,
  SEARCH_PARAM_SLOW_SECTION_ID,
  SEARCH_PARAM_SORT_BY,
  SEARCH_PARAM_STATION_ID,
  SEARCH_PARAM_STATION_SEARCH,
  SEARCH_PARAM_VEREINA_STATUS,
  SEARCH_PARAMS_ROUTE_ID,
} from '@/utils/constants/queries';
import isLandscape from '@/utils/helpers/is-landscape';

const PARAMS_TO_CLEAR = [
  SEARCH_PARAM_SHOW,
  SEARCH_PARAM_STATION_ID,
  SEARCH_PARAM_JOURNEY_ID,
  SEARCH_PARAMS_ROUTE_ID,
  SEARCH_PARAM_ROUTE_LINE_NUMBER,
  SEARCH_PARAM_INCIDENT_ID,
  SEARCH_PARAM_VEREINA_STATUS,
  SEARCH_PARAM_STATION_SEARCH,
  SEARCH_PARAM_ROUTE_SEARCH,
  SEARCH_PARAM_INCIDENT_SEARCH,
  SEARCH_PARAM_INCIDENTS,
  SEARCH_PARAM_ROUTE_TYPE,
  SEARCH_PARAM_SORT_BY,
  SEARCH_PARAM_SELECTED_DATE,
  SEARCH_PARAM_MONITOR,
];

const SidePanel: FC<SidePanelProps> = ({
  header,
  viewType,
  isSidePanelOpen = true,
  hasBackIcon = false,
  hasHeaderBottomBorder = false,
  setSidePanelOpen,
  children,
  footer,
  className,
  onClickBackIcon,
  isFullscreen,
  hasError,
}) => {
  const [isSidePanelVisible, setIsSidePanelVisible] = useState(isSidePanelOpen);
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile: boolean = useBreakpoint('mobile');
  const isLaptop: boolean = useBreakpoint('laptop');
  const isMobileLandscape = isLaptop && isLandscape();
  const isSlowSectionVisible: boolean = !!searchParams.get(SEARCH_PARAM_SLOW_SECTION_ID);
  const currentLockedId = searchParams.get(SEARCH_PARAM_LOCK_PIN);
  const isRelatedIncidentList = !!searchParams.get(SEARCH_PARAM_INCIDENTS);
  const isDrawer = !!searchParams.get(SEARCH_PARAM_DRAWER);

  const { removeFromLast, clearChain } = useChainStore();

  const panelSide: 'left' | 'bottom' = isMobile ? 'bottom' : 'left';

  const isBackNavigationRef = useRef<boolean>(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const shouldNotifyCloseRef = useRef<boolean>(false);

  const isShowFooter = footer && !isMobileLandscape;

  useEffect(() => {
    setIsSidePanelVisible(isSidePanelOpen);

    if (isSidePanelOpen) {
      setSearchParams(
        (prev) => {
          if (isSlowSectionVisible) {
            prev.delete(SEARCH_PARAM_SLOW_SECTION_ID);
          }

          if (isDrawer) {
            prev.delete(SEARCH_PARAM_DRAWER);
          }

          return prev;
        },
        { replace: true }
      );
    }
  }, [isSidePanelOpen, isSlowSectionVisible, isDrawer]);

  const { handleScrollCapture } = useScrollRestoration({
    viewType,
    wrapperRef,
    isBackNavigation: isBackNavigationRef,
  });

  const handleRequestClose = () => {
    shouldNotifyCloseRef.current = true;
    setIsSidePanelVisible(false);
    clearChain();
  };

  const handleBackClick = () => {
    isBackNavigationRef.current = true;

    if (isRelatedIncidentList) {
      onClickBackIcon?.();

      return;
    }

    const value = removeFromLast();

    if (value) {
      setSearchParams((prev) => {
        PARAMS_TO_CLEAR.forEach((param) => prev.delete(param));

        Object.entries(value).forEach(([key, val]) => {
          if (val) {
            prev.set(key, val as string);
          }
        });

        return prev;
      });
    } else {
      onClickBackIcon?.();
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      handleRequestClose();
    }
  };

  const handleAfterClose = () => {
    if (shouldNotifyCloseRef.current) {
      shouldNotifyCloseRef.current = false;

      setSidePanelOpen?.(false);

      setSearchParams(
        (prev) => {
          PARAMS_TO_CLEAR.forEach((param) => prev.delete(param));

          if (currentLockedId) {
            prev.set(SEARCH_PARAM_DRAWER, 'true');
          }

          return prev;
        },
        { replace: true }
      );
    }
  };

  return (
    <Sheet open={isSidePanelVisible} modal={false} onOpenChange={handleOpenChange}>
      <SheetContent
        onAnimationEnd={handleAfterClose}
        className={cn(
          'transition-all duration-300 ease-in-out w-100',
          { 'min-w-dvw': isFullscreen },
          {
            'rounded-t-[15px] h-[calc(100dvh-4rem)] bottom-[52px] w-[calc(100dvw-24px)] left-1/2 -translate-x-1/2':
              isMobile,
          },
          {
            'top-16 h-[calc(100vh-7.2rem)]': !isMobile,
          },
          { 'min-w-[400px]': !isMobile && !isFullscreen },
          className
        )}
        side={panelSide}
        onInteractOutside={(e) => e.preventDefault()}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="flex items-center pl-4 gap-1">
          {hasBackIcon && (
            <Button
              size="icon"
              variant="ghost"
              onClick={handleBackClick}
              className="cursor-pointer p-0 size-5 hover:bg-white"
              name="back-btn"
              aria-label="back-btn"
            >
              <ChevronLeftIcon className="size-6 text-black" />
            </Button>
          )}

          <SheetHeader className="text-xl text-card-foreground py-4 px-0 font-semibold">
            {header}
          </SheetHeader>
        </div>
        {hasHeaderBottomBorder && <div className="border-b mx-4 -my-1" />}
        <div className="flex flex-col flex-1 w-full overflow-hidden">
          {hasError ? (
            <div className="h-full flex items-center px-4 w-full">{children}</div>
          ) : (
            <SidePanelInitialContent wrapperRef={wrapperRef} onScrollCapture={handleScrollCapture}>
              {children}
            </SidePanelInitialContent>
          )}
        </div>
        {isShowFooter && <SheetFooter className="py-1">{footer}</SheetFooter>}
      </SheetContent>
    </Sheet>
  );
};

export default SidePanel;
